import { useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, User, Monitor, MonitorOff } from 'lucide-react';
import { useCall } from '@/contexts/CallContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCallSettings } from '@/hooks/useCallSettings';

export function GlobalCallHandler() {
  const { t } = useLanguage();
  const { screenSharingEnabled } = useCallSettings();
  const {
    callState,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleScreenShare,
    setLocalVideoRef,
    setRemoteVideoRef,
  } = useCall();

  const localVideoElement = useRef<HTMLVideoElement>(null);
  const remoteVideoElement = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (localVideoElement.current) {
      setLocalVideoRef(localVideoElement.current);
    }
    if (remoteVideoElement.current) {
      setRemoteVideoRef(remoteVideoElement.current);
    }
  }, [setLocalVideoRef, setRemoteVideoRef, callState.isActive]);

  if (!callState.isActive) return null;

  const isVideoCall = callState.callType === 'video';
  
  // Screen sharing is only supported on desktop browsers AND must be enabled in admin settings
  const supportsScreenShare = screenSharingEnabled && 
    typeof navigator !== 'undefined' && 
    navigator.mediaDevices && 
    typeof navigator.mediaDevices.getDisplayMedia === 'function';

  return (
    <Dialog open={callState.isActive} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-background/95 backdrop-blur-lg border-none z-[100]">
        {/* Incoming call UI */}
        {callState.isIncoming && !callState.isConnected && (
          <div className="p-8 text-center">
            <div className="relative mx-auto w-24 h-24 mb-4">
              <Avatar className="w-24 h-24 ring-4 ring-primary/20 animate-pulse">
                <AvatarFallback className="text-2xl bg-primary/10">
                  <User className="h-10 w-10 text-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-2">
                {isVideoCall ? (
                  <Video className="h-4 w-4 text-primary-foreground" />
                ) : (
                  <Phone className="h-4 w-4 text-primary-foreground" />
                )}
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-2">{callState.callerName}</h3>
            <p className="text-muted-foreground mb-8">
              Incoming {isVideoCall ? 'video' : 'audio'} call...
            </p>
            <div className="flex justify-center gap-8">
              <Button
                variant="destructive"
                size="lg"
                className="rounded-full w-16 h-16"
                onClick={rejectCall}
              >
                <PhoneOff className="h-6 w-6" />
              </Button>
              <Button
                size="lg"
                className="rounded-full w-16 h-16 bg-green-500 hover:bg-green-600"
                onClick={acceptCall}
              >
                <Phone className="h-6 w-6" />
              </Button>
            </div>
          </div>
        )}

        {/* Outgoing call UI */}
        {callState.isOutgoing && !callState.isConnected && (
          <div className="p-8 text-center">
            <Avatar className="w-24 h-24 mx-auto mb-4 ring-4 ring-primary/20">
              <AvatarFallback className="text-2xl bg-primary/10">
                <User className="h-10 w-10 text-primary" />
              </AvatarFallback>
            </Avatar>
            <h3 className="text-xl font-semibold mb-2">{callState.callerName}</h3>
            <p className="text-muted-foreground mb-8">Calling...</p>
            <Button
              variant="destructive"
              size="lg"
              className="rounded-full w-16 h-16"
              onClick={endCall}
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </div>
        )}

        {/* Active call UI */}
        {callState.isConnected && (
          <div className="relative">
            {isVideoCall || callState.isScreenSharing ? (
              <div className="relative aspect-video bg-black">
                <video
                  ref={remoteVideoElement}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 right-4 w-32 aspect-video bg-muted rounded-lg overflow-hidden shadow-lg">
                  <video
                    ref={localVideoElement}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {callState.isScreenSharing && (
                    <div className="absolute bottom-1 left-1 bg-primary/80 text-primary-foreground text-xs px-1.5 py-0.5 rounded">
                      Sharing
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <video
                  ref={remoteVideoElement}
                  autoPlay
                  playsInline
                  className="hidden"
                />
                <Avatar className="w-24 h-24 mx-auto mb-4">
                  <AvatarFallback className="text-2xl bg-primary/10">
                    <User className="h-10 w-10 text-primary" />
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-semibold mb-2">{callState.callerName}</h3>
                <p className="text-sm text-green-500">Connected</p>
              </div>
            )}

            <div className={`${isVideoCall || callState.isScreenSharing ? 'absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent' : ''} p-6`}>
              <div className="flex justify-center gap-4">
                <Button
                  variant={callState.isMuted ? 'destructive' : 'secondary'}
                  size="lg"
                  className="rounded-full w-14 h-14"
                  onClick={toggleMute}
                >
                  {callState.isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
                {isVideoCall && (
                  <Button
                    variant={!callState.isVideoEnabled ? 'destructive' : 'secondary'}
                    size="lg"
                    className="rounded-full w-14 h-14"
                    onClick={toggleVideo}
                  >
                    {callState.isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                  </Button>
                )}
                {supportsScreenShare && (
                  <Button
                    variant={callState.isScreenSharing ? 'default' : 'secondary'}
                    size="lg"
                    className="rounded-full w-14 h-14"
                    onClick={toggleScreenShare}
                  >
                    {callState.isScreenSharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="lg"
                  className="rounded-full w-14 h-14"
                  onClick={endCall}
                >
                  <PhoneOff className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
